<script>import Button from "../../../components/Button.svelte";
import { afterUpdate, onMount } from "svelte";
import TlmdDomainCaption from "../../TlmdDomainCaption.svelte";
import ReportConfigurator from "../../report/Configurator.svelte";
import PhysicalPropertyField from "../../../components/form/PhysicalPropertyField.svelte";
import ReportSection from "../ReportSection.svelte";
import { API } from "../../../api/api.js";
import { theme } from "../../../stores/theme.js";
import { toLocale } from "../../../i18n/translatable.js";
import {
  locale as globalLocale,
  timeZone as globalTimeZone,
  timeRange
} from "../global-store.js";
import {} from "../../d3/signal/types.js";
import {
  createModellingRunPlots,
  createReceptorPlots,
  extractReceptors,
  extractSources
} from "./modelling-report-helpers.js";
import ReceptorSection from "./ReceptorSection.svelte";
import ReportFooter from "../ReportFooter.svelte";
import { GoiTypeId } from "../../d3/legacy/modelling/types.js";
import { humanTimeZone } from "../../util/i18n-time-util.js";
import { getDateTimeFormatters } from "../../d3/time-axis/timeline-scale.js";
import { ModellingGrid } from "../../../grid/grid.js";
import TextField from "../../../components/form/TextField.svelte";
export let domain;
export let domainExtName;
export let timeZone = void 0;
export let locale = void 0;
export let rangeStart;
export let rangeEnd;
export let rangeType;
export let fixedRangeType = false;
export let host = void 0;
export let credentials = void 0;
export let modellingTypeId;
export let includeDomain = false;
export let grouping = "source";
export let maxGroupSize = 6;
export let autoColor = true;
export let threshold = 5;
export let assets = "";
let mounted = false;
let started = false;
let dataRange = void 0;
let headerElem;
let headerHeight = 0;
afterUpdate(() => {
  const height = headerElem ? headerElem.getBoundingClientRect().height : 0;
  if (height !== headerHeight) {
    headerHeight = height;
  }
});
$timeRange = { start: new Date(rangeStart), end: new Date(rangeEnd) };
$globalLocale = locale ?? $globalLocale;
$globalTimeZone = timeZone ?? $globalTimeZone;
$:
  reportConfiguration = {
    range: {
      start: $timeRange.start.getTime(),
      end: $timeRange.end.getTime(),
      type: rangeType
    }
  };
let unit = void 0;
let reportThreshold = { value: threshold, unit: "" };
let reportPercentile = 0.98;
let percentileInputValue = !isNaN(reportPercentile) ? (reportPercentile * 100).toString() : void 0;
$: {
  if (percentileInputValue != void 0 && percentileInputValue.trim().length > 0) {
    reportPercentile = Number.parseFloat(percentileInputValue);
    if (!isNaN(reportPercentile)) {
      reportPercentile = Math.min(Math.max(reportPercentile, 0), 100);
      percentileInputValue = reportPercentile.toString();
      reportPercentile /= 100;
    } else {
      reportPercentile = void 0;
    }
  } else {
    reportPercentile = void 0;
    percentileInputValue = void 0;
  }
}
$:
  formatters = getDateTimeFormatters(
    $globalLocale,
    $globalTimeZone,
    "hours"
  );
$:
  startMarker = {
    millis: $timeRange.start.getTime(),
    label: formatters.bound($timeRange.start)
  };
$:
  endMarker = {
    millis: $timeRange.end.getTime(),
    label: formatters.bound($timeRange.end)
  };
$:
  rangeAsText = `${startMarker?.label ?? $timeRange.start} - ${endMarker?.label ?? $timeRange.end}`;
$: {
  if (reportConfiguration.range.end !== reportConfiguration.range.start && reportThreshold?.value !== void 0) {
    getData(reportConfiguration.range, reportThreshold, reportPercentile);
  }
}
let modellingType;
let signalPlotsByReceptor = void 0;
let signalPlotsByModellingRun = void 0;
let receptors;
let sources;
$:
  reportTitle = getReportTitle(modellingType);
onMount(async () => {
  mounted = true;
  console.log("Ready...");
  API.init(host ? host : "");
  API.setDomain(domain);
  if (credentials) {
    API.setCredentials(credentials.usr, credentials.pwd);
  } else {
    API.reloadCredentials();
  }
  await API.signIn();
  modellingType = await API.getModellingType(modellingTypeId);
  const grid = new ModellingGrid(modellingType.grid);
  const goisOfType = await API.getGoisOfType(
    GoiTypeId.ImpactMonitor,
    void 0,
    //active or not
    false
    //not deleted
  );
  receptors = extractReceptors(
    goisOfType,
    goisOfType.length === 0 || includeDomain ? grid : void 0,
    autoColor
  );
  unit = grid.getUnit();
  reportThreshold.unit = unit.getName();
  started = true;
  await getData(reportConfiguration.range, reportThreshold, reportPercentile);
});
const openProperties = (key) => {
  const propertiesDiv = document.querySelector(".report div.properties");
  propertiesDiv.classList.remove("closed");
  let configuratorClassName = key;
  propertiesDiv.querySelectorAll("div.configurator").forEach((elem) => {
    if (elem.classList.contains(configuratorClassName)) {
      elem.classList.add("open");
    } else {
      elem.classList.remove("open");
    }
  });
};
const closeProperties = () => {
  const propertiesDiv = document.querySelector(".report div.properties");
  document.querySelector(".report div.properties")?.classList.add("closed");
};
const getReportTitle = (modellingType2) => {
  if (modellingType2) {
    let title = modellingType2.label;
    return title;
  }
  return "...";
};
const getData = async (range, threshold2, percentile) => {
  const doGetData = started && range && range.start !== void 0 && range.end !== void 0 && (dataRange == void 0 || dataRange.start != range.start || dataRange.end != range.end);
  if (doGetData) {
    dataRange = range;
    const modellingRuns = await API.getModellingRuns(
      new Date(range.start).toISOString(),
      new Date(range.end).toISOString(),
      [modellingTypeId],
      true,
      true,
      //active
      false
      //not deleted
    );
    sources = extractSources(modellingType, modellingRuns);
    signalPlotsByModellingRun = grouping === "source" ? createModellingRunPlots(
      sources,
      receptors,
      threshold2.value,
      percentile,
      $globalLocale,
      maxGroupSize
    ) : void 0;
    signalPlotsByReceptor = grouping === "receptor" ? createReceptorPlots(
      sources,
      receptors,
      threshold2.value,
      percentile,
      $globalLocale,
      maxGroupSize
    ) : void 0;
  } else {
    if (signalPlotsByModellingRun) {
      console.log("signalPlotsByModellingRun", signalPlotsByModellingRun);
      Object.values(signalPlotsByModellingRun).forEach(
        (modellingRunVisualizations) => {
          modellingRunVisualizations.forEach((sourceVisualizations) => {
            sourceVisualizations.forEach((receptorVisualization) => {
              receptorVisualization.properties.percentile = percentile;
              if (receptorVisualization.properties.tags) {
                receptorVisualization.properties.tags[0].value = threshold2.value;
                receptorVisualization.properties.tags[0].label = `>= ${threshold2.value}`;
              }
            });
          });
        }
      );
      signalPlotsByModellingRun = signalPlotsByModellingRun;
    }
    if (signalPlotsByReceptor) {
      console.log("signalPlotsByReceptor", signalPlotsByReceptor);
      Object.values(signalPlotsByReceptor).forEach(
        (signalPlotsByReceptor2) => {
          signalPlotsByReceptor2.forEach((sourceVisualizations) => {
            sourceVisualizations.forEach((processVisualization) => {
              processVisualization.properties.percentile = percentile;
              if (processVisualization.properties.tags) {
                processVisualization.properties.tags[0].value = threshold2.value;
                processVisualization.properties.tags[0].label = `>= ${threshold2.value}`;
              }
            });
          });
        }
      );
      signalPlotsByReceptor = signalPlotsByReceptor;
    }
  }
};
</script>

<svelte:head>
  <title>{reportTitle} Report - {rangeAsText}</title>
</svelte:head>
<!--use this test to avoid seeing light theme first while dark has been chosen (the flicker of brightness) -->
{#if mounted && $globalLocale}
  <div class="report {$theme}" style="--headerHeight:{headerHeight}px;">
    <div class="content">
      <header bind:this={headerElem} class="header">
        <div class="caption">
          <div class="noprint float">
            <Button
              story={"button.primary.medium"}
              on:click={(ev) => window.print()}
              ><span class="glyph-print" /></Button
            >
            <Button
              story={$theme === "dark"
                ? "button.secondary.medium.icon"
                : "button.primary.medium.icon"}
              on:click={(ev) => openProperties("general")}
              ><span class="glyph-cog" /></Button
            >
          </div>
          <TlmdDomainCaption {assets} domain={domainExtName} /> - {reportTitle} Report
        </div>
        <div class="info">
          {rangeAsText} ({humanTimeZone($globalTimeZone, $globalLocale)})
        </div>
        <div class="info">
          <!-- info here (e.g. number of data points)-->
        </div>
      </header>
      <div class="report-body tlmd-scroll">
        <table class="page">
          <thead
            ><!--this will not be visible on screen--><tr
              ><th>
                <header class="header">
                  <div class="caption">
                    <div class="noprint float">
                      <Button
                        story={$theme === "dark"
                          ? "button.secondary.medium.icon"
                          : "button.primary.medium.icon"}
                        on:click={(ev) => openProperties("general")}
                        ><span class="glyph-cog" /></Button
                      >
                    </div>
                    <TlmdDomainCaption {assets} domain={domainExtName} /> - {reportTitle}
                    Report
                  </div>
                  <div class="info">
                    {rangeAsText} ({humanTimeZone(
                      $globalTimeZone,
                      $globalLocale,
                    )})
                  </div>
                  <div class="info">
                    <!-- info here (e.g. number of data points)-->
                  </div>
                </header>
              </th></tr
            ></thead
          >
          <tbody>
            <!--prove of concept code START-->
            {#if signalPlotsByModellingRun}
              {#each Object.keys(signalPlotsByModellingRun) as modellingRunId, i}
                {#if signalPlotsByModellingRun[modellingRunId] && signalPlotsByModellingRun[modellingRunId].length > 0}
                  <ReportSection
                    caption={`Maximums for ${sources.map[modellingRunId].name}`}
                  >
                    {#each signalPlotsByModellingRun[modellingRunId] as plotsBatch}
                      <ReceptorSection plotDefinitions={plotsBatch} />
                    {/each}
                  </ReportSection>
                {/if}
              {/each}
            {/if}
            {#if signalPlotsByReceptor}
              {#each Object.keys(signalPlotsByReceptor) as receptorId}
                {#if signalPlotsByReceptor[receptorId] && signalPlotsByReceptor[receptorId].length > 0}
                  <ReportSection
                    caption={`Maximums ${receptors.map[receptorId].isHole ? "outside" : "in"} ${receptors.map[receptorId].name}`}
                  >
                    {#each signalPlotsByReceptor[receptorId] as plotsBatch}
                      <ReceptorSection plotDefinitions={plotsBatch} />
                    {/each}
                  </ReportSection>
                {/if}
              {/each}
            {/if}

            <!--prove of concept code END-->
          </tbody>
          <ReportFooter />
        </table>
      </div>
    </div>
    <div class="noprint properties right closed">
      <div class="noprint float close">
        <Button
          story={$theme === "dark"
            ? "button.secondary.medium.icon"
            : "button.primary.medium.icon"}
          on:click={closeProperties}><span class="glyph-x" /></Button
        >
      </div>
      <div class="header">
        <span class="caption">Properties</span>
        <!--ThemeSwitch /-->
      </div>
      <div class="configurator general open">
        <ReportConfigurator
          bind:value={reportConfiguration}
          {fixedRangeType}
          locale={toLocale($globalLocale)}
        />
        <div class="configurator-controls">
          {#if unit}
            <PhysicalPropertyField
              name="threshold"
              label="Threshold"
              bind:value={reportThreshold}
              units={[
                { value: unit.getName(), label: unit.getPrefixedSymbol() },
              ]}
            />
          {/if}
          <TextField
            name="percentile"
            label="Percentile"
            bind:value={percentileInputValue}
          />
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  div.report {
    --report-caption-font-size: 24px;
    --report-header-info-font-size: 14px;
    --report-section-header-font-size: 18px;
    --report-chart-header-font-size: 16px;
    --report-footer-font-size: 14px;

    --base-color-bg-default: #2d2f36;
    --base-color-primary-default: #6bae85;
    --base-color-onSurface-secondary: #1a1b1f;
    --base-color-onSurface-primary: #dbe4e7;
    --core-color-crayon-honeyYellow: #fbc62f;
  }

  div.report {
    --surface-color: var(--base-color-onSurface-primary);
    --header-surface-color: var(--core-color-crayon-honeyYellow);
    --header-onSurface-color: var(--base-color-onSurface-secondary);
    --section-onSurface-color: var(--base-color-onSurface-secondary);
    --data-header-onSurface-color: var(--base-color-onSurface-primary);
    --scroll-color: var(--base-color-onSurface-secondary);
  }

  div.report.dark {
    --surface-color: var(--base-color-bg-default);
    --header-surface-color: var(--base-color-onSurface-secondary);
    --header-onSurface-color: var(--base-color-primary-default);
    --section-onSurface-color: var(--base-color-onSurface-primary);
    --data-header-onSurface-color: var(--base-color-bg-default);
    --scroll-color: var(--base-color-primary-default);
  }
  @media print {
    div.report.dark,
    div.report {
      --surface-color: transparent;
      --header-surface-color: transparent;
      --header-onSurface-color: var(--base-color-onSurface-secondary);
      --section-onSurface-color: var(--base-color-onSurface-secondary);
      --data-header-onSurface-color: white;
    }
  }

  .float {
    float: inline-end;
    position: relative;
    z-index: 10000;
  }
  div.properties {
    border-color: var(--section-onSurface-color);
  }
  div.properties.right {
    border-left: 1px solid var(--header-surface-color);
  }
  div.properties.right:not(.closed) {
    min-width: 425px;
  }
  div.properties.top {
    border-bottom: 1px solid var(--header-surface-color);
  }
  div.properties.closed {
    width: 0px;
    border: none;
  }
  div.properties.closed :global(*) {
    position: absolute;
    left: -9999px;
  }

  div.properties div.header {
    background-color: var(--header-surface-color);
    border-left: 2px solid var(--surface-color);
    padding: 1em;
    color: var(--section-onSurface-color);
  }
  div.properties div.header .caption {
    font-weight: 700;
    font-size: 18px;
    margin: 1em 0;
    display: inline-block;
  }

  div.properties .configurator {
    color: var(--section-onSurface-color);
  }

  div.properties div.configurator {
    display: none;
  }
  div.properties div.configurator.open {
    display: block;
  }

  div.properties.closed div.close {
    display: none;
  }
  div.properties div.configurator div.configurator-controls {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }

  div.report {
    background-color: var(--surface-color);
  }

  .report table {
    border: 0;
    border-collapse: collapse;
    width: 100%;
  }

  .report .content > header,
  .report table thead {
    background-color: var(--header-surface-color);
  }
  .report table thead tr th {
    font-weight: normal;
    text-align: unset;
  }

  div.report :global(.tlmd-domain-caption) {
    --domain-font-size: var(--report-caption-font-size);
  }

  .report .content > header,
  .report table thead tr th .header {
    color: var(--header-onSurface-color);
  }
  .report .content > header .caption,
  .report table thead tr th .header .caption {
    font-size: var(--report-caption-font-size);
    font-weight: bold;
  }
  .report .content > header .info,
  .report table thead tr th .header .info {
    font-size: var(--report-header-info-font-size);
    font-weight: bold;
    margin-top: 0.5em;
  }

  .report header.header {
    margin-bottom: 1rem;
  }

  /* .report table tfoot tr td .footer {
    font-size: var(--report-footer-font-size);
    margin-top: 0.5em;
  } */

  /* .report table tbody tr.section td {
    color: var(--section-onSurface-color);
  } */

  .report table tbody tr.section td .header {
    font-size: var(--report-section-header-font-size);
    font-weight: bold;
  }
  .report table tbody tr.section td .time-plot .header,
  .report table tbody tr.section td .chart .header {
    font-size: var(--report-chart-header-font-size);
    font-weight: bold;
  }

  /* .report table tfoot tr td {
    color: var(--section-onSurface-color);
  } */

  .report .content > header,
  .report table thead th {
    padding: 1em;
  }
  /* .report table tfoot td {
    padding: 1em;
  } */
  /* .report table tbody td {
    padding: 0 1em 1em 1em;
  } */
  .report {
    font-family: Roboto;
  }

  div.statistics.pie {
    display: inline-block;
    min-width: 360px;
    width: calc(50% - 1em);
    max-width: 640px;
    margin: 0.5em;
    border: 1px solid var(--section-onSurface-color);
    border-radius: 0.5em;
    padding: 1em;
  }
  /* .report table tbody td :global(svg) {
    color: var(--section-onSurface-color);
  } */

  /* .report div.distribution {
		text-align: center;
	} */

  .time-plot .header {
    text-align: center;
    margin-top: 2rem;
  }
  @media screen {
    .report {
      display: grid;
    }

    .report {
      grid-template-columns: 1fr auto;
    }

    .report > div.content > div.report-body {
      max-height: calc(100vh - var(--headerHeight) - 0px);
      overflow-y: auto;
    }

    /* Styling the scrollbar in Firefox  */
    .tlmd-scroll {
      scrollbar-color: var(--scroll-color) transparent;
      scrollbar-width: thin;
    }
    /* Styling the scrollbar in Chrome and Safari */
    .tlmd-scroll::-webkit-scrollbar {
      width: 3px;
    }
    .tlmd-scroll::-webkit-scrollbar-thumb {
      background-color: var(
        --scroll-color
      ); /* Set thumb color for Chrome and Safari */
    }
    .tlmd-scroll::-webkit-scrollbar-track {
      background-color: transparent; /* Set track color for Chrome and Safari */
    }

    .report .content {
      grid-column: 1 / 2;
    }

    .report .properties {
      grid-column: 2 / 3;
    }

    .report table :global(svg.plot) {
      max-width: 1292px;
    }
    .report table thead {
      display: none;
    }
    .not-included {
      -webkit-filter: blur(5px) grayscale(1);
      -moz-filter: blur(5px) grayscale(1);
      -o-filter: blur(5px) grayscale(1);
      -ms-filter: blur(5px) grayscale(1);
      filter: blur(5px) grayscale(1);
    }
  }

  @media print {
    .report {
      display: block;
    }
    .report .content > header {
      display: none;
    }
    .not-included,
    .noprint {
      display: none;
      position: absolute;
      border: none;
      left: -9999px;
    }
    div.statistics.pie {
      min-width: 0;
    }
    div.properties.right.closed,
    div.properties.right {
      min-width: 0;
      width: 0;
    }
  }
</style>
